import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Download, X, Check, X as XIcon, RefreshCw } from "lucide-react";
import type { ProposalStatus } from "@gsp/shared";
import { api } from "../../lib/api";
import { useVendors, usePlants, useErpSystems } from "../../lib/useReference";
import { useToast } from "../../lib/toast";
import { formatDate } from "../../lib/dateFormat";
import { useDebounce } from "../../lib/useDebounce";
import { Card } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { FieldSelect, FieldInput, DateFormatRadios } from "../../components/ui/Field";
import { ProposalStatusBadge } from "../../components/ui/StatusBadge";

interface Proposal {
  id: number;
  poLineId: number;
  creationDate: string;
  poNumber: string;
  poLineItem: number;
  materialNumber: string;
  materialDescription: string;
  originalQty: number;
  proposedQty: number;
  originalDeliveryDate: string;
  proposedDeliveryDate: string;
  reasonId: number | null;
  status: ProposalStatus;
  unit: string;
  plantId: number;
  vendorId: number;
  unitPrice: number | null;
  currency: string;
  paymentTerms: string | null;
  mrpControllerNumber: string | null;
  mrpControllerName: string | null;
  regionalBuyer: string | null;
  regionalBuyerName: string | null;
  sapLineSequence: string | null;
  proposalCreationDate: string;
  proposalCreatedByEmail: string | null;
}

export function PoProposals() {
  const [vendorId, setVendorId] = useState("");
  const [plantId, setPlantId] = useState("");
  const [materialNumber, setMaterialNumber] = useState("");
  const [poNumberFilter, setPoNumberFilter] = useState("");
  const [erpId, setErpId] = useState("");
  const [dateFormat, setDateFormat] = useState("DD/MMM/YYYY");
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [comment, setComment] = useState("");

  const { data: vendors } = useVendors();
  const { data: plants } = usePlants();
  const { data: erpSystems } = useErpSystems();
  const toast = useToast();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const vendorMap = useMemo(() => new Map((vendors ?? []).map((v) => [v.id, v.vendorNumber])), [vendors]);
  const plantMap = useMemo(() => new Map((plants ?? []).map((p) => [p.id, p.plantNumber])), [plants]);

  const materialNumberDebounced = useDebounce(materialNumber);
  const poNumberDebounced = useDebounce(poNumberFilter);

  const query = useQuery({
    queryKey: ["po-proposals", vendorId, plantId, materialNumberDebounced, poNumberDebounced, erpId],
    queryFn: () =>
      api.get<{ rows: Proposal[] }>(
        `/po/proposals?${new URLSearchParams({
          ...(vendorId && { vendorId }),
          ...(plantId && { plantId }),
          ...(materialNumberDebounced && { materialNumber: materialNumberDebounced }),
          ...(poNumberDebounced && { poNumber: poNumberDebounced }),
          ...(erpId && { erpId }),
        })}`
      ),
  });

  const decisionMutation = useMutation({
    mutationFn: (decision: "approve" | "reject") =>
      api.post("/po/proposals/decision", { proposalIds: [...selected], decision, comment: comment.trim() || undefined }),
    onSuccess: (_, decision) => {
      toast(decision === "approve" ? "PO's Approved successfully" : "PO's Rejected successfully");
      setSelected(new Set());
      setComment("");
      qc.invalidateQueries({ queryKey: ["po-proposals"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
    onError: () => toast("Action failed", "error"),
  });

  const rows = query.data?.rows ?? [];

  const activeFilters = useMemo(
    () =>
      [
        vendorId && { key: "vendorId", label: `Vendor: ${vendorMap.get(Number(vendorId)) ?? vendorId}`, clear: () => setVendorId("") },
        plantId && { key: "plantId", label: `Plant: ${plantMap.get(Number(plantId)) ?? plantId}`, clear: () => setPlantId("") },
        materialNumber && { key: "materialNumber", label: `Material: ${materialNumber}`, clear: () => setMaterialNumber("") },
        poNumberFilter && { key: "poNumber", label: `PO: ${poNumberFilter}`, clear: () => setPoNumberFilter("") },
        erpId && { key: "erpId", label: `ERP: ${erpId}`, clear: () => setErpId("") },
      ].filter(Boolean) as Array<{ key: string; label: string; clear: () => void }>,
    [vendorId, plantId, materialNumber, poNumberFilter, erpId, vendorMap, plantMap]
  );

  return (
    <div>
      <Card className="mb-5">
        <div className="p-5 grid grid-cols-2 md:grid-cols-5 gap-4">
          <FieldSelect label="Vendor Number" value={vendorId} onChange={(e) => setVendorId(e.target.value)} options={[{ value: "", label: "All vendors" }, ...(vendors ?? []).map((v) => ({ value: v.id, label: `${v.vendorNumber} - ${v.name}` }))]} />
          <FieldSelect label="Plant Number" value={plantId} onChange={(e) => setPlantId(e.target.value)} options={[{ value: "", label: "All plants" }, ...(plants ?? []).map((p) => ({ value: p.id, label: `${p.plantNumber} - ${p.name}` }))]} />
          <FieldInput label="Material Number" value={materialNumber} onChange={(e) => setMaterialNumber(e.target.value)} />
          <FieldInput label="PO Number" value={poNumberFilter} onChange={(e) => setPoNumberFilter(e.target.value)} />
          <FieldSelect label="ERPID" value={erpId} onChange={(e) => setErpId(e.target.value)} options={[{ value: "", label: "All ERP systems" }, ...(erpSystems ?? []).map((e) => ({ value: e.name, label: e.name }))]} />
        </div>
        {activeFilters.length > 0 && (
          <div className="px-5 pb-5 flex flex-wrap gap-2">
            {activeFilters.map((f) => (
              <button key={f.key} onClick={f.clear} className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-full bg-slate-100 dark:bg-white/10 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-white/20">
                {f.label}
                <XIcon className="w-3 h-3" />
              </button>
            ))}
          </div>
        )}
      </Card>

      <Card>
        <div className="p-4 flex flex-wrap items-center gap-3 border-b border-slate-100 dark:border-white/10">
          <div className="font-semibold text-sm shrink-0">Supplier PO Proposals</div>
          <div className="shrink-0">
            <DateFormatRadios value={dateFormat} onChange={setDateFormat} />
          </div>
          <div className="ml-auto flex gap-2 items-center">
            <button
              onClick={() => query.refetch()}
              disabled={query.isFetching}
              title="Refresh"
              aria-label="Refresh"
              className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-white/5 outline-none focus-visible:ring-2 focus-visible:ring-sbd-yellow/60 disabled:opacity-50"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${query.isFetching ? "animate-spin" : ""}`} />
            </button>
            <Button variant="outline" icon={Download} onClick={() => api.download("/po/export/proposals", "Supplier_Proposals.xlsx")}>
              Export to Excel
            </Button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="data w-full text-xs">
            <thead className="bg-slate-50 dark:bg-white/5 text-slate-500">
              <tr>
                <th className="p-3 text-left">
                  <input type="checkbox" checked={rows.length > 0 && selected.size === rows.length} onChange={(e) => setSelected(e.target.checked ? new Set(rows.map((r) => r.id)) : new Set())} />
                </th>
                <th className="p-3 text-left">Proposal Creation Date</th>
                <th className="p-3 text-left">PO Number</th>
                <th className="p-3 text-left">Status</th>
                <th className="p-3 text-left">Material</th>
                <th className="p-3 text-left">Original Delivery</th>
                <th className="p-3 text-left">Proposed Delivery</th>
                <th className="p-3 text-right">Original Qty</th>
                <th className="p-3 text-right">Proposed Qty</th>
                <th className="p-3 text-left">Plant</th>
                <th className="p-3 text-left">Vendor</th>
                <th className="p-3 text-right">Unit Price</th>
                <th className="p-3 text-left">Currency</th>
                <th className="p-3 text-left">Payment Terms</th>
                <th className="p-3 text-left">MRP Cont.</th>
                <th className="p-3 text-left">MRP Cont. Name</th>
                <th className="p-3 text-left">Regional Buyer</th>
                <th className="p-3 text-left">Regional Buyer Name</th>
                <th className="p-3 text-left">Proposed By</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-slate-100 dark:border-white/5">
                  <td className="p-3">
                    <input
                      type="checkbox"
                      checked={selected.has(r.id)}
                      onChange={() =>
                        setSelected((prev) => {
                          const next = new Set(prev);
                          next.has(r.id) ? next.delete(r.id) : next.add(r.id);
                          return next;
                        })
                      }
                    />
                  </td>
                  <td className="p-3">{formatDate(r.proposalCreationDate, dateFormat)}</td>
                  <td className="p-3">
                    {r.poNumber}-{r.poLineItem}
                  </td>
                  <td className="p-3">
                    <ProposalStatusBadge status={r.status} />
                  </td>
                  <td className="p-3">
                    {r.materialNumber} — {r.materialDescription}
                  </td>
                  <td className="p-3">{formatDate(r.originalDeliveryDate, dateFormat)}</td>
                  <td className="p-3">{formatDate(r.proposedDeliveryDate, dateFormat)}</td>
                  <td className="p-3 text-right">{r.originalQty}</td>
                  <td className="p-3 text-right">{r.proposedQty}</td>
                  <td className="p-3">{plantMap.get(r.plantId) ?? r.plantId}</td>
                  <td className="p-3">{vendorMap.get(r.vendorId) ?? r.vendorId}</td>
                  <td className="p-3 text-right">{r.unitPrice?.toFixed(2)}</td>
                  <td className="p-3">{r.currency}</td>
                  <td className="p-3">{r.paymentTerms}</td>
                  <td className="p-3">{r.mrpControllerNumber}</td>
                  <td className="p-3">{r.mrpControllerName}</td>
                  <td className="p-3">{r.regionalBuyer}</td>
                  <td className="p-3">{r.regionalBuyerName}</td>
                  <td className="p-3">{r.proposalCreatedByEmail}</td>
                </tr>
              ))}
              {!rows.length && (
                <tr>
                  <td colSpan={19} className="p-8 text-center text-slate-400">
                    No pending supplier proposals.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="p-4 border-t border-slate-100 dark:border-white/10">
          <label className="text-[11px] font-semibold text-slate-500 dark:text-slate-400">Comment (applied to all selected proposals)</label>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={2}
            className="mt-1 w-full text-sm border border-slate-200 dark:border-white/10 rounded-lg px-3 py-2 bg-white dark:bg-white/5 outline-none focus:ring-2 focus:ring-sbd-yellow/50"
            placeholder="Optional comment for the approve/reject decision"
          />
        </div>
        <div className="p-4 flex justify-end gap-2 border-t border-slate-100 dark:border-white/10">
          <Button variant="ghost" onClick={() => navigate("/")}>
            Back
          </Button>
          <Button variant="danger" icon={X} disabled={!selected.size || decisionMutation.isPending} onClick={() => decisionMutation.mutate("reject")}>
            Reject
          </Button>
          <Button icon={Check} disabled={!selected.size || decisionMutation.isPending} onClick={() => decisionMutation.mutate("approve")}>
            Approve
          </Button>
        </div>
      </Card>
    </div>
  );
}
